import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  INamedEntityRecognitionLabel,
  getLabelColorPalette,
  IColorMap,
} from './namedEntityRecognitionLabels';
import ReactTooltip from 'react-tooltip';
import { depthFirstLabels } from '@scale/shared/namedentityrecognition/label';
// eslint-disable-next-line restrict-imports/restrict-imports
import { Divider } from '@material-ui/core';
import AnnotationAttributesPicker from '../../AnnotationAttributesPicker';
import { Text } from 'design-system';
import { AnnotationChange } from '@scale/shared/annotation/types/AnnotationChange';
import { AttributeSelections, NERGlobalAttribute } from '@scale/shared/types/annotation';
import TaxonomyInstructionPopper from '../../Sidebar/TaxonomyNodeInstructionpPopper';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

interface ISidebarProps {
  colorMap: IColorMap;
  labels: INamedEntityRecognitionLabel[];
  hoverredLabelId: string | null;
  setHoverredLabelId: (id: string | null) => void;
  onChange: (_annotationId: string | undefined, change: AnnotationChange) => void;
  isLabelRequired: boolean;
  isReadOnly: boolean;
  attrDefinitions?: { [key: string]: NERGlobalAttribute };
  attrSelections?: AttributeSelections;
}

export const NamedEntityRecognitionSidebar: React.FC<ISidebarProps> = props => {
  const { labels } = props;

  const attrDefinitions = props.attrDefinitions ? props.attrDefinitions : {};
  const attrSelections = props.attrSelections ? props.attrSelections : {};
  const [hidingGlobalAttributes, setHidingGlobalAttributes] = React.useState<boolean>(false);

  const hasGlobalAttributes = attrDefinitions ? Object.keys(attrDefinitions).length !== 0 : false;

  const toggleHidingGlobalAttributes = () => {
    setHidingGlobalAttributes(!hidingGlobalAttributes);
  };

  const [expanded, setExpanded] = React.useState<string[]>(() => {
    const names: string[] = [];
    for (const label of depthFirstLabels(labels)) {
      if (label.children) {
        names.push(label.name);
      }
    }
    return names;
  });

  const onExpand = (name: string) => {
    setExpanded(_.union(expanded, [name]));
  };

  const onCollapse = (name: string) => {
    setExpanded(_.without(expanded, name));
  };

  return (
    <>
      <div className="label-list">
        {hasGlobalAttributes && (
          <>
            <div className="global-attributes-panel" onClick={toggleHidingGlobalAttributes}>
              <Text medium>Global Attributes</Text>
              <FontAwesomeIcon icon={hidingGlobalAttributes ? 'caret-left' : 'caret-down'} />
            </div>
            {!hidingGlobalAttributes && (
              <div className="global-attributes">
                <AnnotationAttributesPicker
                  onChange={props.onChange}
                  isLabelRequired={props.isLabelRequired}
                  isReadOnly={props.isReadOnly}
                  attrDefinitions={attrDefinitions}
                  attrSelections={attrSelections}
                />
              </div>
            )}
          </>
        )}
        {props.labels.map(label => {
          if (label.children) {
            const hidden = !expanded.includes(label.name);
            return (
              <div key={label.name} className="label-category-list">
                <div className="label-category">
                  <span style={{ marginRight: 8 }}>{label.name}</span>
                  {hidden ? (
                    <button onClick={() => onExpand(label.name)} className="expand-label-button">
                      <FontAwesomeIcon icon="chevron-down" style={{ fontSize: 12 }} />
                    </button>
                  ) : (
                    <button onClick={() => onCollapse(label.name)} className="expand-label-button">
                      <FontAwesomeIcon icon="chevron-up" style={{ fontSize: 12 }} />
                    </button>
                  )}
                </div>
                {!hidden &&
                  label.children.map(childLabel => {
                    return (
                      <NamedEntityRecognitionLabelListLabel
                        {...props}
                        key={childLabel.name}
                        label={childLabel}
                      />
                    );
                  })}
              </div>
            );
          }

          return <NamedEntityRecognitionLabelListLabel {...props} key={label.name} label={label} />;
        })}

        <style jsx>{`
          .label-list {
            display: block;
            height: 100%;
            padding: 16px;
            padding-bottom: 100px;
            overflow: scroll;
            min-width: 424px;
            max-width: 424px;
            height: 100%;
            border-left: 1px solid #e6e6e6;
          }
          .label-category {
            display: flex;
            flex-direction: row;
            align-items: center;
            font-weight: 500;
            font-size: 14px;
            padding: 8px 8px 8px 8px;
            margin: 8px;
          }
          .label-category-list {
            background: white;
            border-radius: 12px;
            margin-bottom: 8px;
            box-shadow: 0 0px 4px rgba(0, 0, 0, 0.1);
            overflow: auto;
          }
          .expand-label-button {
            display: inline-flex;
            flex-direction: row;
            align-items: center;
            background: none;
            border: 0;
            cursor: pointer;
            color: rgba(0, 0, 0, 0.5);
          }
          .global-attributes {
            padding: 16px;
            overflow-y: scroll;
            min-height: 100px;
            background: white;
            border-radius: 12px;
            box-shadow: 0 0px 4px rgba(0, 0, 0, 0.1);
          }

          .global-attributes-panel {
            padding: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-weight: 500;
            border-bottom: 1px solid var(--color-RemoGray01);
            cursor: pointer;
          }
        `}</style>
      </div>
    </>
  );
};

let timer: any = null;

const NamedEntityRecognitionLabelListLabel: React.FC<
  ISidebarProps & {
    label: INamedEntityRecognitionLabel;
  }
> = ({ label, colorMap, hoverredLabelId, setHoverredLabelId }) => {
  const popRef = useRef(null);
  const [show, setShow] = useState<boolean>(false);
  const labelColorPalette = React.useMemo(
    () => getLabelColorPalette(label.name, colorMap),
    [label, colorMap],
  );
  const showContextualTrigger = useUnleashFeatureFlag('NGI-contextual-triggering', {}, false);

  const dataTipName = `label-description-${label.name}`;
  const hasDescription = !!label.description;

  return (
    <>
      <div
        className="label-list-label"
        key={label.name}
        ref={popRef}
        onMouseEnter={() => {
          timer = setTimeout(() => {
            setShow(true);
          }, 2000);
          setHoverredLabelId(label.name);
        }}
        onMouseLeave={() => {
          clearTimeout(timer);
          setShow(false);
          setHoverredLabelId(null);
        }}
        style={{
          borderColor:
            hoverredLabelId === label.name
              ? labelColorPalette.listItemHoverredBackground
              : 'transparent',
        }}
        data-tip={hasDescription || undefined}
        data-for={dataTipName}
      >
        {showContextualTrigger && (
          <TaxonomyInstructionPopper title={label.name} popRef={popRef} show={show} />
        )}
        <div className="label-list-label-header">
          <div
            className="label-list-label-color-square"
            style={{ backgroundColor: labelColorPalette.listItemBackground }}
            // style={{ backgroundColor: labelColorPalette.original }} // this is the orig light color
          />
          <div className="label-names-wrapper">
            <div className="label-list-label-name">{label.display_name || label.name}</div>
          </div>
        </div>
      </div>

      <ReactTooltip id={dataTipName} place="bottom" delayShow={500}>
        <div style={{ width: '240px', lineHeight: '1.5em', whiteSpace: 'normal' }}>
          {label.description}
        </div>
      </ReactTooltip>
      <Divider />

      <style jsx>{`
        .label-list-label {
          user-select: none;
          min-height: 20px;
          padding-left: 16px;
          border: solid 3px;
          border-radius: 12px;
          margin: 8px;
          background: white;
        }
        .label-list-label-header {
          display: flex;
          margin-top: 8px;
          margin-bottom: 8px;
          align-items: center;
        }
        .label-list-label-color-square {
          border-radius: 4px;
          width: 16px;
          height: 16px;
        }
        .label-list-label-name {
          font-weight: 500;
          padding-left: 17px;
          font-size: 14px;
        }
        .label-list-display-name {
          font-weight: 100;
          padding-left: 16px;
          font-size: 13px;
          color: #aaaaaa;
        }
        .label-info {
          padding-bottom: 16px;
        }
        .label-info-text {
          font-size: 12px;
          color: rgba(0, 0, 0, 1);
          line-height: 16px;
        }
      `}</style>
    </>
  );
};
